/**
 * R2 asset manifest — schema version 2 (authoring/R2_MANIFEST_SCHEMA_V2.md).
 *
 * One key schedule for every asset and every rung: absent values are explicit
 * null, never an omitted key. Every `file` is relative to the manifest's own
 * directory, every draw list is complete and already z-sorted, and hidden
 * layers are simply absent from the list — nothing subtracts, nothing joins by
 * name. The pure resolution rules at the bottom of this file are the port of
 * authoring/r2-runtime-proof/index.html; they carry no renderer dependency.
 */
export const SCHEMA_VERSION = 2;
export const MIRROR_RULE_ANCHORS = 'x -> width - x';
export const MIRROR_RULE_RASTER = 'flip horizontally';

export type Vec2 = readonly [number, number];
export type Facing = 'left' | 'right';
export type EntryDelta = 'none' | 'raster';

export interface MirrorRule {
  readonly anchors: string;
  readonly raster: string;
}

export interface RuntimeContract {
  readonly authoringRungs: readonly string[];
  /** null while the shipped sprite is the authoring raster; a smaller runtime rung is a separate step. */
  readonly runtimeReduction: unknown;
}

/** The part of an entry the renderer actually paints. */
export interface DrawEntry {
  readonly name: string;
  readonly z: number;
  readonly file: string;
  readonly offset: Vec2;
  readonly size: Vec2;
  readonly px: number;
  readonly sha256: string;
}

/** `layers[]` — one master layer (18 keys). */
export interface MasterLayer extends DrawEntry {
  readonly emissive: boolean;
  readonly ownedPx: number;
  readonly fillBehindPx: number;
  readonly hostedPx: number;
  readonly marginPx: number;
  readonly unhostedPx: number;
  readonly hostedBy: readonly string[];
  readonly hostsFillBehindFor: readonly string[];
  readonly rootMarginOn: string | null;
  readonly repairedCells: number;
  readonly orphansMerged: number;
}

/** One item of a state's or a pulse frame's draw list (9 keys). */
export interface StackEntry extends DrawEntry {
  readonly delta: EntryDelta;
  readonly changedPx: number;
}

/** Pulse frame (7 keys), top level and per state. */
export interface PulseFrame {
  readonly frame: number;
  readonly amp: number;
  readonly params: Readonly<Record<string, number>>;
  readonly layers: readonly StackEntry[];
  readonly composite: string;
  readonly silhouettePx: number;
  readonly bodySilhouettePx: number;
}

export interface StatePulse {
  readonly frames: readonly PulseFrame[];
}

/** `states[name]` (13 keys). `pulse` is null iff the state hides a layer the pulse drives. */
export interface ManifestState {
  readonly label: string;
  readonly note: string | null;
  readonly delta: EntryDelta;
  readonly hidden: readonly string[];
  readonly measuredRatios: unknown;
  readonly appliedAmp: number | null;
  readonly ember: boolean;
  readonly layers: readonly StackEntry[];
  readonly changedPx: number;
  readonly composite: string;
  readonly silhouettePx: number;
  readonly silhouetteMatchesMaster: boolean;
  readonly pulse: StatePulse | null;
}

/**
 * Top level (36 keys, all present in all rungs). Reduction bookkeeping, palette
 * derivation and provenance are typed as `unknown`: the schema states their
 * innards are per-family and not normalised, and no loader reads them.
 */
export interface LayerStackManifest {
  readonly schemaVersion: number;
  readonly asset: string;
  readonly slug: string;
  readonly rung: string;
  readonly label: string;
  readonly generator: string;
  readonly facing: Facing;
  readonly mirrorRule: MirrorRule;
  readonly runtimeContract: RuntimeContract;
  readonly target: Vec2;
  readonly block: unknown;
  readonly intermediate: unknown;
  readonly colors: unknown;
  readonly colorBudget: unknown;
  readonly seed: unknown;
  readonly snapDe: unknown;
  readonly knobs: unknown;
  readonly ramps: unknown;
  readonly recessDim: unknown;
  readonly reference: unknown;
  readonly palette: unknown;
  readonly paletteInfo: unknown;
  readonly anchors: Readonly<Record<string, Vec2>>;
  readonly calloutAnchors: readonly unknown[];
  readonly composite: string;
  readonly layers: readonly MasterLayer[];
  readonly states: Readonly<Record<string, ManifestState>>;
  readonly pulse: readonly PulseFrame[];
  readonly silhouettePx: number;
  readonly fillBehind: unknown;
  readonly stackProof: unknown;
  readonly pulseDistinct: boolean;
  readonly pulsePairwiseDeltaPx: unknown;
  readonly stateAmplitudeGain: number | null;
  readonly stateAmplitudeClamp: number | null;
  readonly notes: unknown;
}

/** Master stack when the state name is null, otherwise that state's stack. */
export type StateName = string | null;

function fail(source: string, detail: string): never {
  throw new Error(`${source}: ${detail}`);
}

/**
 * Structural gate on the fields the renderer depends on. The pipeline produces
 * the uniformity, so this checks the contract rather than re-deriving it.
 */
export function assertLayerStackManifest(value: unknown, source: string): LayerStackManifest {
  if (typeof value !== 'object' || value === null) fail(source, 'manifest is not an object');
  const manifest = value as LayerStackManifest;
  if (manifest.schemaVersion !== SCHEMA_VERSION) {
    fail(source, `schemaVersion ${String(manifest.schemaVersion)}, expected ${SCHEMA_VERSION}`);
  }
  if (!Array.isArray(manifest.target) || manifest.target.length !== 2) fail(source, 'target must be [w, h]');
  if (manifest.facing !== 'left' && manifest.facing !== 'right') fail(source, `facing ${String(manifest.facing)}`);
  if (manifest.mirrorRule?.anchors !== MIRROR_RULE_ANCHORS || manifest.mirrorRule.raster !== MIRROR_RULE_RASTER) {
    fail(source, `unsupported mirrorRule ${JSON.stringify(manifest.mirrorRule)}`);
  }
  if (!Array.isArray(manifest.layers) || manifest.layers.length === 0) fail(source, 'layers is empty');
  if (!Array.isArray(manifest.pulse)) fail(source, 'pulse must be an array');
  if (typeof manifest.states !== 'object' || manifest.states === null) fail(source, 'states must be an object');
  return manifest;
}

export function manifestState(manifest: LayerStackManifest, state: StateName): ManifestState | null {
  if (state === null) return null;
  const found = manifest.states[state];
  if (!found) throw new Error(`${manifest.slug} ${manifest.rung}: unknown state "${state}"`);
  return found;
}

export function stateNames(manifest: LayerStackManifest): string[] {
  return Object.keys(manifest.states);
}

/**
 * The pulse cycle that belongs to this state, or null when there is none:
 * a state declares `pulse: null` when it hides a layer the pulse drives, and a
 * handful of assets ship an empty master cycle. Both mean "do not animate".
 */
export function pulseFrames(manifest: LayerStackManifest, state: StateName): readonly PulseFrame[] | null {
  const frames = state === null ? manifest.pulse : manifestState(manifest, state)?.pulse?.frames;
  return frames && frames.length > 0 ? frames : null;
}

/** The whole v2 read path: one already-ordered list, painted at each entry's offset. */
export function drawList(manifest: LayerStackManifest, state: StateName, frame: number | null): readonly DrawEntry[] {
  const frames = frame === null ? null : pulseFrames(manifest, state);
  if (frames !== null && frame !== null) return frames[frame % frames.length]!.layers;
  return state === null ? manifest.layers : manifestState(manifest, state)!.layers;
}

/** Every draw list the manifest can hand a renderer — the loader's fetch set. */
export function allDrawLists(manifest: LayerStackManifest): readonly (readonly DrawEntry[])[] {
  const lists: (readonly DrawEntry[])[] = [manifest.layers];
  for (const state of Object.values(manifest.states)) {
    lists.push(state.layers);
    if (state.pulse) for (const entry of state.pulse.frames) lists.push(entry.layers);
  }
  for (const entry of manifest.pulse) lists.push(entry.layers);
  return lists;
}

export function allFileFields(manifest: LayerStackManifest): string[] {
  const files = new Set<string>();
  for (const list of allDrawLists(manifest)) for (const entry of list) files.add(entry.file);
  return [...files];
}

/** manifest.mirrorRule.anchors, applied. Anchors live in `target` space. */
export function mirrorAnchorX(manifest: LayerStackManifest, x: number): number {
  return manifest.target[0] - x;
}
